import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  Plus,
  Search,
  Trash2,
  ClipboardList,
  Weight,
  CalendarDays,
  ChevronDown,
  X,
  AlertTriangle,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LineItemSummary = {
  product_name: string;
  size_category: string;
  size_category_name: string | null;
  quantity: number;
  quantity_type: string;
};

type WorkOrder = {
  id: number;
  wo_number: string;
  date: string | null;
  created_at: string;
  client_id?: number | null;
  client_name?: string | null;
  product_name: string;
  size_category: string;
  quantity: number;
  weight_lbs: number | null;
  total_weight_lbs: number | null;
  status: string;
  priority: "low" | "normal" | "high" | "urgent";
  order_type: string | null;
  line_items: LineItemSummary[];
  line_count: number;
  status_updated_at?: string | null;
};

type StatusFilter = "all" | "pending" | "acknowledged" | "blending" | "blend_complete" | "awaiting_pickup" | "out_for_delivery" | "delivered" | "on_hold" | "cancelled";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  pending:          { label: "Pending",           bg: "bg-gray-100 dark:bg-gray-800",           text: "text-gray-700 dark:text-gray-300" },
  acknowledged:     { label: "Acknowledged",      bg: "bg-blue-100 dark:bg-blue-900/30",        text: "text-blue-700 dark:text-blue-400" },
  scheduled:        { label: "Scheduled",         bg: "bg-blue-100 dark:bg-blue-900/30",        text: "text-blue-700 dark:text-blue-400" },
  blending:         { label: "Blend in Progress", bg: "bg-amber-100 dark:bg-amber-900/30",      text: "text-amber-700 dark:text-amber-400" },
  in_progress:      { label: "In Progress",       bg: "bg-orange-100 dark:bg-orange-900/30",    text: "text-orange-700 dark:text-orange-400" },
  blend_complete:   { label: "Blend Complete",    bg: "bg-emerald-100 dark:bg-emerald-900/30",  text: "text-emerald-700 dark:text-emerald-400" },
  awaiting_pickup:  { label: "Awaiting Pickup",   bg: "bg-purple-100 dark:bg-purple-900/30",    text: "text-purple-700 dark:text-purple-400" },
  out_for_delivery: { label: "Out for Delivery",  bg: "bg-indigo-100 dark:bg-indigo-900/30",    text: "text-indigo-700 dark:text-indigo-400" },
  delivered:        { label: "Delivered",         bg: "bg-green-100 dark:bg-green-900/30",      text: "text-green-700 dark:text-green-400" },
  completed:        { label: "Completed",         bg: "bg-green-100 dark:bg-green-900/30",      text: "text-green-700 dark:text-green-400" },
  on_hold:          { label: "On Hold",           bg: "bg-red-100 dark:bg-red-900/30",          text: "text-red-700 dark:text-red-400" },
  cancelled:        { label: "Cancelled",         bg: "bg-gray-100 dark:bg-gray-800",           text: "text-gray-500 dark:text-gray-500" },
};
const UNKNOWN_STATUS = { label: "—", bg: "bg-gray-100 dark:bg-gray-800", text: "text-gray-600 dark:text-gray-400" };

const PRIORITY_CONFIG: Record<
  WorkOrder["priority"],
  { label: string; bg: string; text: string; hidden?: boolean }
> = {
  low: { label: "Low", bg: "bg-gray-100 dark:bg-gray-800", text: "text-gray-600 dark:text-gray-400" },
  normal: { label: "Normal", bg: "", text: "", hidden: true },
  high: { label: "High", bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-400" },
  urgent: { label: "Urgent", bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400" },
};

// Order type (channel) labels for list display
const ORDER_TYPE_LABELS: Record<string, string> = {
  production: "Production",
  "Amazon FBA": "Amazon FBA",
  "FBA labels": "FBA labels",
  "Local Sales": "Local Sales",
  "Local sales": "Local Sales",
  wholesale: "Wholesale",
  Wholesale: "Wholesale",
  "Bulk Blend": "Bulk Blend",
  "Bulk blend": "Bulk Blend",
  bulk: "Bulk",
  amazon: "Amazon",
};

function formatOrderType(orderType: string | null | undefined): string {
  if (!orderType || !orderType.trim()) return "—";
  const key = orderType.trim();
  return ORDER_TYPE_LABELS[key] ?? key.replace(/^\w/, (c) => c.toUpperCase());
}

// Default column widths (px) for resizable table (no Actions column)
// Client, Product, Size set wider so names don’t truncate or overlap
const DEFAULT_WO_COL_WIDTHS = [40, 140, 100, 200, 300, 160, 64, 88, 100, 110];
const MIN_COL_WIDTH = 48;

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return "--";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "--";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatWeight(lbs: number | null | undefined) {
  if (lbs === null || lbs === undefined) return "--";
  const n = Number(lbs);
  if (isNaN(n) || n === 0) return "--";
  return n.toLocaleString("en-US");
}

function isThisWeek(dateStr: string | null | undefined) {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return false;
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 7);
  return date >= startOfWeek && date < endOfWeek;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function WorkOrderList() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Bulk selection
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);

  // Confirm delete modal (bulk)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Resizable column widths (px)
  const [colWidths, setColWidths] = useState<number[]>(() => DEFAULT_WO_COL_WIDTHS);
  const [resizingCol, setResizingCol] = useState<number | null>(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------

  const fetchWorkOrders = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      if (search.trim()) params.set("search", search.trim());

      const qs = params.toString();
      const url = `/api/ops/work-orders${qs ? `?${qs}` : ""}`;

      const res = await apiFetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`Failed to load work orders (${res.status})`);

      const data = await res.json();
      setWorkOrders(data.workOrders ?? data.work_orders ?? []);
    } catch (err: any) {
      if (!silent) setError(err?.message || "Failed to load work orders");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
  }, [token, statusFilter, dateFrom, dateTo]);

  // Real-time polling — silent refresh every 15s
  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchWorkOrders(true);
    }, 15000);
    return () => clearInterval(id);
  }, [token, statusFilter, dateFrom, dateTo, search]);

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchWorkOrders();
    }, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  // Column resize: global mouse move/up when dragging
  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (resizingCol === null) return;
    const delta = e.clientX - resizeStartX.current;
    setColWidths((prev) => {
      const next = [...prev];
      const newW = Math.max(MIN_COL_WIDTH, resizeStartWidth.current + delta);
      next[resizingCol] = newW;
      return next;
    });
  }, [resizingCol]);

  const handleResizeEnd = useCallback(() => {
    setResizingCol(null);
  }, []);

  const handleResetColumnWidth = useCallback((colIndex: number) => {
    setColWidths((prev) => {
      const next = [...prev];
      next[colIndex] = DEFAULT_WO_COL_WIDTHS[colIndex];
      return next;
    });
  }, []);

  useEffect(() => {
    if (resizingCol === null) return;
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", handleResizeMove);
    window.addEventListener("mouseup", handleResizeEnd);
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
      window.removeEventListener("mousemove", handleResizeMove);
      window.removeEventListener("mouseup", handleResizeEnd);
    };
  }, [resizingCol, handleResizeMove, handleResizeEnd]);

  // -----------------------------------------------------------------------
  // Filtered & computed
  // -----------------------------------------------------------------------

  const filtered = useMemo(() => {
    return workOrders;
  }, [workOrders]);

  const totalWeight = useMemo(
    () => filtered.reduce((sum, wo) => sum + (Number(wo.total_weight_lbs) || Number(wo.weight_lbs) || 0), 0),
    [filtered]
  );

  const thisWeekCount = useMemo(
    () => workOrders.filter((wo) => isThisWeek(wo.created_at || wo.date)).length,
    [workOrders]
  );

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((wo) => wo.id)));
    }
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const res = await apiFetch("/api/ops/work-orders/delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: Array.from(selected) }),
      });
      if (!res.ok) throw new Error("Delete failed");
      setSelected(new Set());
      setShowDeleteConfirm(false);
      await fetchWorkOrders();
    } catch (err: any) {
      setError(err?.message || "Failed to delete work orders");
    } finally {
      setDeleting(false);
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-4 max-w-7xl">
      {/* Header + stats inline */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-4 flex-wrap">
          <h1 className="heading-display text-2xl text-foreground">Work Orders</h1>
          {!loading && (
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <ClipboardList size={14} className="text-forest-500" />
                <strong className="text-foreground tabular-nums">{workOrders.length}</strong> WOs
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Weight size={14} className="text-amber-500" />
                <strong className="text-foreground tabular-nums">{totalWeight.toLocaleString("en-US")}</strong> lbs
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <CalendarDays size={14} className="text-blue-500" />
                <strong className="text-foreground tabular-nums">{thisWeekCount}</strong> this week
              </span>
            </div>
          )}
        </div>
        <Link
          to="/app/ops/work-orders/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
        >
          <Plus size={16} strokeWidth={2.5} />
          New Work Order
        </Link>
      </div>

      {/* Filters row */}
      <div className="bg-card border border-border rounded-xl p-3 space-y-2.5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          {/* Search */}
          <label className="flex items-center gap-2 px-3 bg-background border border-border rounded-lg">
            <Search size={15} className="text-muted-foreground shrink-0" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search WO#, product..."
              className="w-full py-2 text-sm bg-transparent outline-none text-foreground placeholder:text-muted-foreground/60"
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-muted-foreground hover:text-foreground">
                <X size={14} />
              </button>
            )}
          </label>

          {/* Status filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground appearance-none pr-8"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="blending">Blend in Progress</option>
              <option value="blend_complete">Blend Complete</option>
              <option value="awaiting_pickup">Awaiting Pickup</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="on_hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <ChevronDown
              size={14}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
          </div>

          {/* Date from */}
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="From date"
            className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground"
          />

          {/* Date to */}
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="To date"
            className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground"
          />
        </div>

        {/* Status pill tabs */}
        <div className="flex gap-1 flex-wrap">
          {(
            [
              { key: "all", label: "All" },
              { key: "pending", label: "Pending" },
              { key: "acknowledged", label: "Acknowledged" },
              { key: "blending", label: "Blending" },
              { key: "awaiting_pickup", label: "Awaiting Pickup" },
              { key: "out_for_delivery", label: "Out for Delivery" },
              { key: "delivered", label: "Delivered" },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                statusFilter === tab.key
                  ? "bg-forest-600 text-white shadow-sm"
                  : "bg-secondary text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
          <span className="text-sm font-medium text-red-700 dark:text-red-400">
            {selected.size} selected
          </span>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors"
          >
            <Trash2 size={13} />
            Delete Selected
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-muted-foreground hover:text-foreground ml-auto"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle size={16} className="text-red-600 dark:text-red-400 shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading work orders...</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <ClipboardList size={40} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">
              {search || statusFilter !== "all" || dateFrom || dateTo
                ? "No work orders match your filters."
                : "No work orders yet. Create your first one."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ tableLayout: "fixed", minWidth: colWidths.reduce((a, b) => a + b, 0) }}>
              <colgroup>
                {colWidths.map((w, i) => (
                  <col key={i} style={{ width: w, minWidth: w }} />
                ))}
              </colgroup>
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 sticky left-0 z-10 bg-card" style={{ width: colWidths[0], minWidth: colWidths[0] }}>
                    <input
                      type="checkbox"
                      checked={selected.size === filtered.length && filtered.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-border"
                    />
                    <div
                      role="presentation"
                      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-forest-400/30 active:bg-forest-500/50 shrink-0"
                      style={{ touchAction: "none" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setResizingCol(0);
                        resizeStartX.current = e.clientX;
                        resizeStartWidth.current = colWidths[0];
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResetColumnWidth(0);
                      }}
                    />
                  </th>
                  <th className="px-3 py-2 sticky z-10 bg-card border-r border-border/50" style={{ width: colWidths[1], minWidth: colWidths[1], left: colWidths[0] }}>
                    WO#
                    <div
                      role="presentation"
                      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-forest-400/30 active:bg-forest-500/50 shrink-0"
                      style={{ touchAction: "none" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setResizingCol(1);
                        resizeStartX.current = e.clientX;
                        resizeStartWidth.current = colWidths[1];
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResetColumnWidth(1);
                      }}
                    />
                  </th>
                  <th className="px-3 py-2 relative">
                    Date
                    <div
                      role="presentation"
                      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-forest-400/30 active:bg-forest-500/50 shrink-0"
                      style={{ touchAction: "none" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setResizingCol(2);
                        resizeStartX.current = e.clientX;
                        resizeStartWidth.current = colWidths[2];
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResetColumnWidth(2);
                      }}
                    />
                  </th>
                  <th className="px-3 py-2 relative">
                    Client
                    <div
                      role="presentation"
                      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-forest-400/30 active:bg-forest-500/50 shrink-0"
                      style={{ touchAction: "none" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setResizingCol(3);
                        resizeStartX.current = e.clientX;
                        resizeStartWidth.current = colWidths[3];
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResetColumnWidth(3);
                      }}
                    />
                  </th>
                  <th className="px-3 py-2 relative">
                    Product
                    <div
                      role="presentation"
                      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-forest-400/30 active:bg-forest-500/50 shrink-0"
                      style={{ touchAction: "none" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setResizingCol(4);
                        resizeStartX.current = e.clientX;
                        resizeStartWidth.current = colWidths[4];
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResetColumnWidth(4);
                      }}
                    />
                  </th>
                  <th className="px-3 py-2 relative">
                    Size
                    <div
                      role="presentation"
                      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-forest-400/30 active:bg-forest-500/50 shrink-0"
                      style={{ touchAction: "none" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setResizingCol(5);
                        resizeStartX.current = e.clientX;
                        resizeStartWidth.current = colWidths[5];
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResetColumnWidth(5);
                      }}
                    />
                  </th>
                  <th className="px-3 py-2 text-right relative">
                    Qty
                    <div
                      role="presentation"
                      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-forest-400/30 active:bg-forest-500/50 shrink-0"
                      style={{ touchAction: "none" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setResizingCol(6);
                        resizeStartX.current = e.clientX;
                        resizeStartWidth.current = colWidths[6];
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResetColumnWidth(6);
                      }}
                    />
                  </th>
                  <th className="px-3 py-2 text-right relative">
                    Weight (lbs)
                    <div
                      role="presentation"
                      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-forest-400/30 active:bg-forest-500/50 shrink-0"
                      style={{ touchAction: "none" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setResizingCol(7);
                        resizeStartX.current = e.clientX;
                        resizeStartWidth.current = colWidths[7];
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResetColumnWidth(7);
                      }}
                    />
                  </th>
                  <th className="px-3 py-2 relative">
                    Status
                    <div
                      role="presentation"
                      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-forest-400/30 active:bg-forest-500/50 shrink-0"
                      style={{ touchAction: "none" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setResizingCol(8);
                        resizeStartX.current = e.clientX;
                        resizeStartWidth.current = colWidths[8];
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResetColumnWidth(8);
                      }}
                    />
                  </th>
                  <th className="px-3 py-2 relative">
                    Order type
                    <div
                      role="presentation"
                      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-forest-400/30 active:bg-forest-500/50 shrink-0"
                      style={{ touchAction: "none" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setResizingCol(9);
                        resizeStartX.current = e.clientX;
                        resizeStartWidth.current = colWidths[9];
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResetColumnWidth(9);
                      }}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((wo) => {
                  const statusCfg = STATUS_CONFIG[wo.status] || { ...UNKNOWN_STATUS, label: wo.status || "—" };
                  const isSelected = selected.has(wo.id);

                  return (
                    <tr
                      key={wo.id}
                      onClick={() => navigate(`/app/ops/work-orders/${wo.id}`)}
                      className={`border-b border-border/60 last:border-0 transition-colors cursor-pointer ${
                        isSelected
                          ? "bg-forest-50/50 dark:bg-forest-950/20"
                          : "hover:bg-muted/30"
                      }`}
                    >
                      <td className="px-3 py-2 sticky left-0 z-10 bg-inherit" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(wo.id)}
                          className="rounded border-border"
                        />
                      </td>
                      <td className="px-3 py-2 sticky z-10 bg-inherit border-r border-border/50" style={{ left: colWidths[0] }}>
                        <span className="font-semibold text-forest-600 dark:text-forest-400 font-mono text-sm whitespace-nowrap hover:underline">
                          {wo.wo_number?.replace(/^WO-/, '#') || wo.wo_number}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                        {formatDate(wo.created_at || wo.date)}
                      </td>
                      <td className="px-3 py-2 text-foreground min-w-0">
                        <span className="block truncate" title={wo.client_name || undefined}>
                          {wo.client_name || <span className="text-muted-foreground/50">--</span>}
                        </span>
                      </td>
                      <td className="px-3 py-2 min-w-0">
                        {wo.line_items && wo.line_items.length > 0 ? (
                          <div className="space-y-0.5 min-w-0">
                            {wo.line_items.slice(0, 3).map((li, idx) => (
                              <div key={idx} className="flex items-center gap-1.5 text-sm min-w-0">
                                <span className="font-medium text-foreground truncate min-w-0" title={li.product_name}>{li.product_name}</span>
                                <span className="text-xs text-muted-foreground shrink-0">
                                  {li.quantity} {li.quantity_type}
                                </span>
                              </div>
                            ))}
                            {wo.line_items.length > 3 && (
                              <span className="text-xs text-muted-foreground">+{wo.line_items.length - 3} more</span>
                            )}
                          </div>
                        ) : (
                          <span className="font-medium text-foreground truncate block min-w-0" title={wo.product_name}>{wo.product_name}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground min-w-0">
                        <span className="block truncate" title={wo.line_items && wo.line_items.length > 0
                          ? wo.line_items.map(li => li.size_category_name || li.size_category).filter(Boolean).join(", ") || wo.size_category || undefined
                          : wo.size_category || undefined}>
                          {wo.line_items && wo.line_items.length > 0
                            ? wo.line_items.map(li => li.size_category_name || li.size_category).filter(Boolean).join(", ") || wo.size_category
                            : wo.size_category}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-foreground tabular-nums">
                        {wo.quantity}
                      </td>
                      <td className="px-3 py-2 text-right text-foreground tabular-nums">
                        {formatWeight(wo.total_weight_lbs || wo.weight_lbs)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusCfg.bg} ${statusCfg.text}`}
                        >
                          {statusCfg.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground text-xs">
                        {formatOrderType(wo.order_type)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bulk delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <Trash2 size={18} className="text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">Delete Work Orders</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
                </div>
              </div>
              <p className="text-sm text-foreground">
                Are you sure you want to permanently delete{" "}
                <strong>{selected.size}</strong> work order{selected.size !== 1 ? "s" : ""}?
              </p>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-border">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={deleting}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                {deleting ? "Deleting..." : `Delete ${selected.size} WO${selected.size !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
